import { Box, Flex, Tabs, TabList, Tab, TabPanels, TabPanel, Text } from '@chakra-ui/react';
import withAuth from '@/HOCs/withAuth';
import { useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGetProductCount } from '@/services/manageAddons';
import { ActiveAddOns } from './ActiveAddOns';
import { OutOfStockAddons } from './OutOfStock';
import { QualityControl } from './QualityControl';
import { RejectedAddons } from './RejectedAddons';

interface IProductCount {
  active_product_count: number;
  out_of_stock_count: number;
  quality_control_count: number;
  rejected_count: number;
}

const ManageAddons = () => {
  const { state } = useLocation();

  const [sharedFilters, setSharedFilters] = useState<{
    start_date?: string;
    end_date?: string;
    category?: number | string;
    // subcategory?: number | string;
  }>({
    start_date: '',
    end_date: '',
    category: '',
    // subcategory: '',
  });

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [visitedTabs, setVisitedTabs] = useState<Set<number>>(new Set());

  const { data: productCount = {} as IProductCount, isLoading: countsLoading } =
    useGetProductCount(sharedFilters); // ← now passes filters!

  const tabCounts = useMemo(() => {
    return {
      active: productCount.active_product_count || 0,
      outOfStock: productCount.out_of_stock_count || 0,
      qc: productCount.quality_control_count || 0,
      rejected: productCount.rejected_count || 0,
    };
  }, [productCount]);

  useEffect(() => {
    if (state?.tabIndex !== undefined && state.tabIndex !== selectedTabIndex) {
      setSelectedTabIndex(state.tabIndex);
    }
  }, [state?.tabIndex]);

  // Update visited tabs when selection changes
  useEffect(() => {
    setVisitedTabs((prev) => new Set(prev).add(selectedTabIndex));
  }, [selectedTabIndex]);

  const tabs = useMemo(
    () => [
      {
        label: 'Active',
        key: 'active' as const,
        component: ActiveAddOns,
      },
      {
        label: 'Out Of Stock',
        key: 'outOfStock' as const,
        component: OutOfStockAddons,
      },
      {
        label: 'Quality Control',
        key: 'qc' as const,
        component: QualityControl,
      },
      {
        label: 'Rejected',
        key: 'rejected' as const,
        component: RejectedAddons,
      },
    ],
    [],
  );

  const handleTabChange = useCallback((index: number) => {
    setSelectedTabIndex(index);
  }, []);

  return (
    <Flex pt={6} direction="column" width="100%" fontFamily={'primary'} bg={'background.white'}>
      <Tabs index={selectedTabIndex} onChange={handleTabChange}>
        <TabList justifyContent="space-around" width="100%">
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              width="100%"
              fontSize="sm"
              fontWeight="semibold"
              color="text.primary"
              _selected={{
                borderBottom: '2px solid',
                borderColor: 'brand.primary.DEFAULT',
                color: 'brand.primary.DEFAULT',
              }}
              _hover={{ color: 'brand.primary.DEFAULT' }}
              _focus={{
                boxShadow: 'none',
                color: 'brand.primary.DEFAULT',
                borderBottom: '2px solid',
                borderColor: 'brand.primary.DEFAULT',
              }}
              _active={{ color: 'brand.primary.DEFAULT' }}
              _focusVisible={{ boxShadow: 'none', outline: 'none' }}
            >
              {tab.label}

              <Box
                minWidth="24px"
                minHeight="24px"
                borderRadius="50%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="xs"
                fontWeight="bold"
                ml={2}
                bg={selectedTabIndex === index ? '#A71C2914' : 'none'}
                color={selectedTabIndex === index ? 'brand.primary.DEFAULT' : '#344054'}
                border="2px solid"
                borderColor={
                  selectedTabIndex === index ? 'brand.primary.DEFAULT' : 'border.default'
                }
              >
                <Text
                  lineHeight={1.2}
                  color={selectedTabIndex === index ? 'brand.primary.DEFAULT' : '#344054'}
                  p={0.5}
                >
                  {countsLoading ? '…' : tabCounts[tab.key]}
                </Text>
              </Box>
            </Tab>
          ))}
        </TabList>

        <TabPanels bg={'background.secondary'}>
          {tabs.map((tab, index) => (
            <TabPanel key={index} p={0}>
              {visitedTabs.has(index) && (
                <tab.component filters={sharedFilters} setFilters={setSharedFilters} />
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Flex>
  );
};

export default withAuth(ManageAddons);
